import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useAtom } from 'jotai';
import moment from 'moment';
import {
  type ColumnFiltersState,
  type SortingState,
} from '@tanstack/react-table';
import { Checkmark } from '@carbon/react/icons';
import { Link } from 'components/link/link';
import { DataTable, type DataTableColumnDef } from 'components/data-table';
import type {
  InitialTableState,
  TableState,
} from 'components/data-table/types';
import {
  createPackageLibrarySearchAtom,
  type PackageLibraryParams,
} from './atoms';
import type {
  InterestType,
  PackageLibraryResult,
  PackageType,
  SummaryCardType,
} from './types';
import { PackageLibraryFilters } from './package-library-filters';
import { PackageLibrarySummaryCards } from './summary-cards';
import { SellersCell } from './sellers-cell';
import {
  DEFAULT_PARAMS,
  INTEREST_TYPE_LABELS,
  PACKAGE_TYPE_LABELS,
  SORT_FIELD_MAP,
  columnFiltersToParams,
} from './utils';
import styles from './package-library-content.module.scss';

// ─────────────────────────────────────
// Column Definitions
// ─────────────────────────────────────

type PackageRow = PackageLibraryResult & Record<string, unknown>;

const renderBoolean = (value: unknown) => {
  return value ? <Checkmark size={20} /> : '--';
};

const columns: DataTableColumnDef<PackageRow>[] = [
  {
    accessorKey: 'name',
    header: 'Name',
    size: 280,
    cell: ({ row }) => <Link to={row.original.href}>{row.original.name}</Link>,
  },
  {
    // Keeps the `sellerName` column id so sorting still sends the BE's
    // `sellerName` sort attribute (SORT_FIELD_MAP).
    id: 'sellerName',
    accessorFn: (row) => row.sellers.map((seller) => seller.name).join(', '),
    header: 'Seller',
    size: 200,
    meta: { type: 'text' },
    cell: ({ row }) => <SellersCell sellers={row.original.sellers} />,
  },
  {
    id: 'acquirerName',
    accessorFn: (row) => row.acquirerEntities.map((e) => e.name).join(', '),
    header: 'Acquire Entity',
    size: 200,
    meta: { type: 'text' },
    cell: ({ row }) => {
      const entities = row.original.acquirerEntities;
      if (entities.length === 0) return '--';
      return (
        <div className={styles.stackedEntities}>
          {entities.map((e) => (
            <Link key={e.id} to={e.href}>
              {e.name}
            </Link>
          ))}
        </div>
      );
    },
  },
  {
    accessorKey: 'type',
    header: 'Package Type',
    size: 160,
    meta: { type: 'enum' },
    cell: ({ getValue }) => {
      const value = getValue() as PackageType | null;
      return value ? (PACKAGE_TYPE_LABELS[value] ?? value) : '--';
    },
  },
  {
    id: 'interestType',
    accessorFn: (row) => row.interestTypes.join(','),
    header: 'Interest Type',
    size: 160,
    meta: { type: 'enum' },
    cell: ({ row }) => {
      const types = row.original.interestTypes;
      if (types.length === 0) return '--';
      return (
        <div className={styles.stackedEntities}>
          {types.map((t) => (
            <span key={t}>
              {INTEREST_TYPE_LABELS[t as InterestType] ??
                `${t.charAt(0).toUpperCase()}${t.slice(1)}`}
            </span>
          ))}
        </div>
      );
    },
  },
  {
    accessorKey: 'closeDate',
    header: 'Close Date',
    size: 140,
    meta: { type: 'date' },
  },
  {
    accessorKey: 'totalAcreage',
    header: 'Total Acreage',
    size: 140,
    meta: {
      type: 'decimal',
      format: { minDecimalPlaces: 2, maxDecimalPlaces: 2 },
    },
  },
  {
    accessorKey: 'containsInventoryAssets',
    header: 'Contains Inventory',
    size: 160,
    meta: { type: 'enum' },
    cell: ({ getValue }) => renderBoolean(getValue()),
  },
  {
    accessorKey: 'effectiveDate',
    header: 'Effective Date',
    size: 140,
    meta: { type: 'date' },
  },
  {
    accessorKey: 'includeChildren',
    header: 'Include Children',
    size: 140,
    meta: { type: 'boolean' },
    cell: ({ getValue }) => renderBoolean(getValue()),
  },
  {
    accessorKey: 'completedEvals',
    header: 'Completed Evals',
    size: 140,
    meta: { type: 'integer' },
  },
  {
    accessorKey: 'workspaceCount',
    header: 'Workspaces',
    size: 120,
    meta: { type: 'integer' },
  },
  {
    accessorKey: 'createdAt',
    header: 'Created Date',
    size: 160,
    meta: { type: 'date' },
  },
  {
    accessorKey: 'updatedAt',
    header: 'Updated Date',
    size: 160,
    meta: { type: 'date' },
  },
];

const initialTableState: InitialTableState = {
  columnPinning: { left: ['name'] },
  sortColumn: 'createdAt',
  sortDirection: 'desc',
};

// ─────────────────────────────────────
// Content Component
// ─────────────────────────────────────

const PackageLibraryContent: FC = () => {
  const { searchAtom } = useMemo(() => createPackageLibrarySearchAtom(), []);
  const [searchResult, setSearchPayload] = useAtom(searchAtom);

  const [params, setParams] = useState<PackageLibraryParams>(DEFAULT_PARAMS);

  useEffect(() => {
    setSearchPayload(() => params);
  }, [params, setSearchPayload]);

  const updateParams = useCallback((updates: Partial<PackageLibraryParams>) => {
    setParams((current) => ({ ...current, page: 1, ...updates }));
  }, []);

  const handleColumnFilterChange = useCallback(
    (filters: ColumnFiltersState) => {
      updateParams(columnFiltersToParams(filters));
    },
    [updateParams]
  );

  const handleSortChange = useCallback(
    (sorting: SortingState) => {
      if (sorting.length === 0) {
        updateParams({ sortAttribute: 'createdAt', sortDirection: 'DESC' });
      } else {
        const sort = sorting[0];
        updateParams({
          sortAttribute: SORT_FIELD_MAP[sort.id] ?? sort.id,
          sortDirection: sort.desc ? 'DESC' : 'ASC',
        });
      }
    },
    [updateParams]
  );

  const handleGlobalSearchChange = useCallback(
    (search: string) => {
      const newText = search || undefined;
      if (params.text === newText) return;
      updateParams({ text: newText });
    },
    [params.text, updateParams]
  );

  const [activeCard, setActiveCard] = useState<SummaryCardType | null>(null);

  const clearCardFilters = (): Partial<PackageLibraryParams> => ({
    containsInventoryAssets: undefined,
    createdAtStart: undefined,
    createdAtEnd: undefined,
  });

  const handleSummaryCardClick = useCallback(
    (card: SummaryCardType) => {
      // Toggle off if same card clicked again
      if (activeCard === card) {
        setActiveCard(null);
        updateParams(clearCardFilters());
        return;
      }

      setActiveCard(card);
      const cleared = clearCardFilters();

      if (card === 'opportunity') {
        updateParams({ ...cleared, containsInventoryAssets: false });
        return;
      }
      if (card === 'inventory') {
        updateParams({ ...cleared, containsInventoryAssets: true });
        return;
      }
      const now = moment();
      const daysBack = { last24h: 1, last7d: 7, lastMonth: 30 };
      const start = now.clone().subtract(daysBack[card], 'days');
      updateParams({
        ...cleared,
        createdAtStart: start.format('YYYY-MM-DD'),
        createdAtEnd: now.format('YYYY-MM-DD'),
      });
    },
    [updateParams, activeCard]
  );

  const results = searchResult.data?.results ?? [];
  const totalCount = searchResult.data?.totalCount ?? 0;

  const handleLoadData = useCallback(({ page, pageSize }: TableState) => {
    setParams((current) => ({ ...current, page, pageSize }));
  }, []);

  return (
    <div className={styles.contentWrapper}>
      <PackageLibrarySummaryCards
        aggregations={searchResult.data?.aggregations}
        onCardClick={handleSummaryCardClick}
      />
      <div className={styles.tableWrapper}>
        <DataTable<PackageRow>
          data={results as PackageRow[]}
          columns={columns as DataTableColumnDef<PackageRow>[]}
          getRowId={(row) => String(row.id)}
          initialTableState={initialTableState}
          persistenceKey="package-library-list-table"
          config={{
            columnFilters: false,
            columnReordering: false,
          }}
          isLoading={searchResult.loading}
          error={searchResult.error ?? null}
          scroll={{ mode: 'paginated', serverDriven: true, totalCount }}
          onPaginationChange={handleLoadData}
          onSortChange={(s) => handleSortChange(s.sorting)}
          onGlobalSearchChange={(s) => handleGlobalSearchChange(s.globalFilter)}
          onFilterChange={(s) => handleColumnFilterChange(s.columnFilters)}
          searchPlaceholder="Search by Package Name"
          renderFilterPanel={() => <PackageLibraryFilters />}
        />
      </div>
    </div>
  );
};

export default PackageLibraryContent;
